import { lazy, Suspense, useEffect, useState } from "react";
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";

// - - - - - C O M P O N E N T S - - -  - -
import Sidebar from "../components/Sidebar/Sidebar";
import Loader from "../components/Loader/Loader";
import { verifyToken } from "../services/auth";
import { toast } from "sonner";
import { ConfigProvider, Layout, theme } from "antd";
import AppHeader from "./AppHeader";
import ReissueCoupons from "../views/admin/ReissueCoupons";

// - - - - - V I E W S - - - - -
const LoaderTwo = lazy(() => import("../components/Loader/LoaderTwo"));

// - - - - -  P A G E S  - - - - -
const Dashboard = lazy(() => import("../views/admin/Dashboard"));
const Overview = lazy(() => import("../views/admin/Overview"));
const Settings = lazy(() => import("../views/admin/Settings"));
const ManageInvoice = lazy(() => import("../views/admin/ManageInvoice"));
const Tables = lazy(() => import("../views/admin/Tables"));
const ManageRefunds = lazy(() => import("../views/admin/ManageRefunds"));
const RefundApprovals = lazy(() => import("../views/admin/RefundApprovals"));
const HypdRefundApprovals = lazy(() =>
  import("../views/admin/HypdRefundApprovals")
);

const CrmTickets = lazy(() => import("../views/admin/CrmTickets"));
const LmRemarks = lazy(() => import("../views/admin/UpdateLmRemarks"));
const UpdatePoFile = lazy(() => import("../views/admin/UpdatePoFile"));
const UnplacedOrderRefunds = lazy(() =>
  import("../views/admin/UnplacedOrderRefunds")
);
const ProActiveReachOut = lazy(() =>
  import("../views/admin/ProActiveReachOut")
);
const SalesReport = lazy(() => import("../views/admin/NewSalesReport"));
const RefundReport = lazy(() => import("../views/admin/RefundReport"));

const RefundUpdate = lazy(() => import("../views/admin/UpdateRefundDetail"));

const Maps = lazy(() => import("../views/admin/Maps"));
const OrderDetails = lazy(() => import("../views/admin/OrderDetails"));

const TicketDetails = lazy(() => import("../views/admin/TicketDetails"));

const { Content } = Layout;

export default function Admin() {
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const token = localStorage.getItem("token");
  const navigate = useNavigate();

  useEffect(() => {
    if (!token || token === "") {
      navigate("/auth/login");
    } else {
      (async () => {
        const res = await verifyToken();
        if (res.status !== 200) {
          localStorage.setItem("session", "expired");
          localStorage.clear();
          navigate("/auth/login");
          toast.error("Seesion Expired, Please Login");
        }
      })();
    }
  }, []);

  const menuItems = {
    "/admin/dashboard": "Dashboard",
    "/admin/overview": "Overview",
    "/admin/orders": "Customer Orders",
    "/admin/invoice": "Invoice",
    "/admin/order-details": "Order Details",
    "/admin/manage-users": "Manage Users",
    "/admin/manage-refunds": "Manage Refunds",
    "/admin/refund-approve": "Refund Approvals",
    "/admin/reissue-coupons": "Reissue Coupons",
    "/admin/crm-tickets": "Dabur CRM Tickets",
    "/admin/lm-remarks-update": "Lm Remarks Update",
    "/admin/po-update": "Po Update",
    "/admin/refund-detail-update": "Refund Detail Update",
    "/admin/unplaced-order": "Order Not Placed Refund",
    "/admin/pro-active-reachout": "Pro Active Reachout",
    "/admin/sales-report": "Sales Report",
    "/admin/refund-report": "Refund Report",
  };

  return (
    <>
      <ConfigProvider
        theme={{
          algorithm: theme.defaultAlgorithm,
          token: {
            colorPrimary: "#619269",
            colorBgContainer: "rgba(255, 255, 255, 0.9)",
            borderRadius: 12,
          },
          components: {
            Layout: {
              siderBg: "rgba(255, 255, 255, 0.95)",
              headerBg: "rgba(255, 255, 255, 0.9)",
            },
            Menu: {
              itemBg: "transparent",
              itemSelectedBg: "#619269",
              itemSelectedColor: "#ffffff",
              itemHoverBg: "rgba(97, 146, 105, 0.1)",
              itemHoverColor: "#619269",
              groupTitleColor: "#8c8c8c",
              groupTitleFontSize: "12px",
            },
          },
        }}
      >
        <Layout
          style={{
            minHeight: "100vh",
            background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
          }}
        >
          <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} />
          <Layout style={{ marginLeft: collapsed ? 80 : 280 }}>
            <AppHeader
              collapsed={collapsed}
              setCollapsed={setCollapsed}
              location={location}
              menuItems={menuItems}
            />
            <Content>
              <div className="md:px-10 pt-9 w-full">
                <Suspense fallback={<Loader />}>
                  <Routes>
                    <Route
                      path="dashboard"
                      element={<Dashboard collapsed={collapsed} />}
                    />
                    <Route path="overview" element={<Overview />} />
                    <Route path="maps" element={<Maps />} />
                    <Route path="orders" element={<Settings />} />
                    <Route path="invoice" element={<ManageInvoice />} />
                    <Route path="manage-users" element={<Tables />} />
                    <Route path="order-details" element={<OrderDetails />} />
                    <Route path="loader" element={<LoaderTwo />} />
                    <Route path="manage-refunds" element={<ManageRefunds />} />
                    <Route
                      path="refund-approve"
                      element={<RefundApprovals />}
                    />
                    <Route
                      path="hypd-refund-approve"
                      element={<HypdRefundApprovals />}
                    />
                    <Route
                      path="reissue-coupons"
                      element={<ReissueCoupons />}
                    />
                    <Route path="crm-tickets" element={<CrmTickets />} />
                    <Route path="lm-remarks-update" element={<LmRemarks />} />
                    <Route path="po-update" element={<UpdatePoFile />} />
                    <Route
                      path="unplaced-order"
                      element={<UnplacedOrderRefunds />}
                    />
                    <Route
                      path="refund-detail-update"
                      element={<RefundUpdate />}
                    />
                    <Route
                      path="pro-active-reachout"
                      element={<ProActiveReachOut />}
                    />

                    <Route path="sales-report" element={<SalesReport />} />
                    <Route path="refund-report" element={<RefundReport />} />
                    <Route path="ticket-detail" element={<TicketDetails />} />
                    <Route
                      path=""
                      element={<Navigate to="/admin/overview" />}
                    />
                  </Routes>
                  {/* <FooterAdmin /> */}
                </Suspense>
              </div>
            </Content>
          </Layout>
        </Layout>
      </ConfigProvider>
    </>
  );
}
